import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { FlexColumn, FlexMiddle } from '../Flex';
import pages from '../../utils/pagination';
import { smallBodySemiBold } from '../Typography';
import { ReactComponent as ArrowIcon } from '../../assets/arrow-right-small.svg';
import ConfigContext from '../../contexts/ConfigContext';
import BaseSelect from '../Select/Select';
import media from '../../style/media';
import { card } from '../Card';
import Table from './Table';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  background: ${({ theme }) => theme.colors.white};
`;

const Container = styled(FlexColumn)`
  flex: 1;
`;

const Footer = styled(FlexMiddle)`
  ${({ theme, shadow }) => (shadow ? card : `border: 1px solid ${theme.colors.grey2};`)}
  z-index: 1;
  justify-content: center;
  padding: 20px 0;

  ${media.mobile`justify-content: flex-end; padding: 15px;`}
`;

const Page = styled.button.attrs(({ $pageButtonType }) => $pageButtonType && { type: $pageButtonType })`
  margin: 0 8px;
  border-radius: 3px;
  padding: 2px 6px;
  ${smallBodySemiBold};
  background: ${({ theme, isActive }) => (isActive ? theme.colors.primary : theme.colors.white)};
  color: ${({ theme, isActive }) => (isActive ? theme.colors.white : theme.colors.black)};
  border: none;
  cursor: ${({ isDisabled }) => (isDisabled ? 'no-drop' : 'pointer')};
  outline: none;

  > svg {
    > path {
      fill: ${({ theme, isDisabled }) => (isDisabled ? theme.colors.grey2 : theme.colors.black)};
    }
  }

  &:hover {
    > svg {
      ${({ isDisabled }) => !isDisabled && 'box-shadow: 0 1px 8px 1px rgba(0, 0, 0, 0.35);'}
    }
  }
`;

const Arrow = styled(ArrowIcon).attrs(({ $reverse }) => ({ className: $reverse ? 'rotate-0' : 'rotate-180' }))`
  background: #fff;
  border-radius: 50%;
  padding: 5px 4px;
  width: 12px;
  margin-top: 7px;
`;

const ResultPerPage = styled(FlexMiddle)`
  gap: 8px;
`;

const Select = styled(BaseSelect)`
  .select-container {
    width: 70px;
    height: 30px;
    padding-right: 0;
  }

  .select-label {
    text-align: center;
    padding: 0;
    flex: 1;
  }

  .select-arrow {
    right: 10px;
  }
`;

const PaginationTable = ({
  page,
  setPage,
  pageButtonType = null,
  setPageSize,
  pageSize = 50,
  totalItems,
  className,
  shadow = true,
  ...restProps
}) => {
  const { t } = useContext(ConfigContext);
  const totalPages = useMemo(() => Math.ceil(totalItems / pageSize), [pageSize, totalItems]);
  const pageNumbers = useMemo(() => pages({ selected: page, pageCount: totalPages }), [page, totalPages]);

  return (
    <Wrapper className={className}>
      <Container>
        <Table {...restProps} shadow={shadow} />
      </Container>

      {(totalPages > 1 || setPageSize) && (
        <Footer shadow={shadow}>
          <>
            <Page
              isDisabled={page === 1}
              $pageButtonType={pageButtonType}
              onClick={() => page > 1 && setPage(page - 1)}>
              <Arrow />
            </Page>

            {pageNumbers.map((_page, index) => {
              const pageNumberIsNotValid = isNaN(_page);
              return (
                <Page
                  key={index}
                  isDisabled={pageNumberIsNotValid}
                  $pageButtonType={pageButtonType}
                  isActive={_page === page}
                  onClick={() => (!pageNumberIsNotValid ? setPage(_page) : undefined)}>
                  {_page}
                </Page>
              );
            })}

            <Page
              isDisabled={page === totalPages}
              $pageButtonType={pageButtonType}
              onClick={() => page < totalPages && setPage(page + 1)}>
              <Arrow $reverse />
            </Page>
          </>
          {setPageSize && (
            <ResultPerPage>
              {t('resultPerPage')}
              <Select
                options={[
                  { value: 50, label: 50 },
                  { value: 100, label: 100 },
                  { value: 150, label: 150 },
                  { value: 200, label: 200 }
                ]}
                value={pageSize}
                onChange={setPageSize}
                searchable={false}
              />
            </ResultPerPage>
          )}
        </Footer>
      )}
    </Wrapper>
  );
};

export default PaginationTable;
